/**
 * StreetEasy market data — the source for every market-level rental figure.
 *
 * Replaces the we3-activities-3 listing-event feed, which turned out to cover too little of the
 * market to report on: its REBNY intake fell ~80% in July 2025 (Manhattan ran ~530 new listings a
 * month against Corcoran's 4,679 signed leases), so medians moved with the feed rather than the
 * market. StreetEasy publishes the aggregate directly.
 *
 * The shape is completely different, and the report has to follow it:
 *   - One document per area per MONTH (`id` = `{area_slug}_{YYYY-MM}`). Nothing is computed at
 *     query time — the medians are already cut for us, so there is no faceting and no date range.
 *     The report reads a single month and compares it to the same month a year earlier.
 *   - Three geography levels only: city (`nyc`), `borough` (5) and `neighborhood` (176). There is
 *     no zip, building or polygon data, so those searches fall back to the borough.
 *   - Coverage runs 2010-01 to the present, monthly.
 *
 * Rental fields carry bedroom cuts (`_all`, `_studio`, `_one_bd`, `_two_bd`, `_three_plus_bd`).
 * The property-type cuts (`_condo`, `_coop`, `_sfr`) belong to the SALES series — including
 * `days_on_market_*`, which is why this report does not show days on market.
 */
import { _capitalizeText } from "./utils";

export const SE_CORE = "streeteasy-market-data";

/**
 * Required source credit. Zillow Group's Terms of Use (§C, Use of Content) permit displaying and
 * distributing derivative works of their aggregate market data for market analysis
 *
 *   "only so long as the Zillow Companies are cited as a source on every page where the Aggregate
 *    Data are displayed, including 'Data Provided by Zillow Group'."
 *
 * So this string is not decoration: the exact phrase has to appear on EVERY page that shows these
 * figures. The same clause bars using their logos without written approval and bars implying any
 * relationship beyond their being the source, which is why this is plain text and says nothing
 * about a partnership.
 *
 * The required phrase "Data Provided by Zillow Group" leads the line and is unaltered. Marketproof
 * is named alongside it at the product owner's direction.
 */
export const SE_SOURCE_CREDIT = "Data Provided by Zillow Group and Marketproof.";

export const SE_BEDROOMS = [
  { key: "studio", label: "Studio" },
  { key: "one_bd", label: "1 Bedroom" },
  { key: "two_bd", label: "2 Bedroom" },
  { key: "three_plus_bd", label: "3+ Bedroom" },
];

/**
 * Boroughs kept out of the comparison table and chart. Staten Island's rental market is too small
 * to sit next to the others — 6 studio listings citywide in July 2026, where a single listing
 * swings the median and produces changes like +67% that read as market moves rather than noise.
 */
export const SE_COMPARISON_EXCLUDE = ["staten-island"];

/**
 * One page per segment. `field` is the suffix on every metric (`median_asking_rent_<field>`).
 *
 * The bedroom pages came back with this dataset: on the old listing-event feed a neighborhood cut
 * by bedroom ran to single digits and the pages were mostly "--", whereas StreetEasy publishes
 * every bedroom cut for all 182 areas.
 */
export const SE_SEGMENTS = [
  { key: "all_rentals", field: "all", name: "Rental Market", title: "All Rental Listings", label: "All Rentals" },
  { key: "studio", field: "studio", name: "Studios", title: "Studio Rentals", label: "Studio" },
  { key: "one_bd", field: "one_bd", name: "One-Bedrooms", title: "One Bedroom Rentals", label: "1 Bedroom" },
  { key: "two_bd", field: "two_bd", name: "Two-Bedrooms", title: "Two Bedroom Rentals", label: "2 Bedroom" },
  { key: "three_plus_bd", field: "three_plus_bd", name: "Three+ Bedrooms", title: "Three Bedroom and Larger Rentals", label: "3+ Bedroom" },
];

export const SE_FL = [
  "area_slug", "area_name", "area_type", "borough", "month", "month_str", "year",
  "median_asking_rent_all", "median_asking_rent_studio", "median_asking_rent_one_bd",
  "median_asking_rent_two_bd", "median_asking_rent_three_plus_bd",
  "rental_inventory_all", "rental_inventory_studio", "rental_inventory_one_bd",
  "rental_inventory_two_bd", "rental_inventory_three_plus_bd",
  "discount_share_all", "discount_share_studio", "discount_share_one_bd",
  "discount_share_two_bd", "discount_share_three_plus_bd",
  "rental_index",
].join(",");

const slugify = (value) =>
  String(value || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/**
 * Our neighborhood vocabulary is finer than StreetEasy's — we carry Lenox Hill, Yorkville and
 * Carnegie Hill where StreetEasy publishes one Upper East Side. These roll ours up to the
 * StreetEasy area that contains them; anything unmapped falls back to the borough, and the report
 * says which geography it actually used.
 */
export const SE_NEIGHBORHOOD_ALIASES = {
  // Manhattan
  "lenox hill": "upper-east-side",
  "yorkville": "upper-east-side",
  "carnegie hill": "upper-east-side",
  "upper carnegie hill": "upper-east-side",
  "lincoln square": "upper-west-side",
  "manhattan valley": "upper-west-side",
  "clinton": "midtown-west",
  "hells kitchen": "midtown-west",
  "hudson yards": "midtown-west",
  "times square": "midtown-west",
  "murray hill": "midtown-east",
  "turtle bay": "midtown-east",
  "sutton place": "midtown-east",
  "beekman": "midtown-east",
  "kips bay": "gramercy-park",
  "rose hill": "gramercy-park",
  "nomad": "midtown-south",
  "west chelsea": "chelsea",
  "harlem": "central-harlem",
  "hudson heights": "washington-heights",
  "fort george": "washington-heights",
  "two bridges": "lower-east-side",
  "fulton seaport": "financial-district",
  "battery park": "battery-park-city",
  "noho": "greenwich-village",
  "bowery": "lower-east-side",
  // Brooklyn
  "prospect lefferts gardens": "prospect-lefferts-gardens",
  "columbia street waterfront district": "columbia-st-waterfront-district",
  "vinegar hill": "dumbo",
  "boerum hill": "boerum-hill",
  "ocean hill": "bedford-stuyvesant",
  "stuyvesant heights": "bedford-stuyvesant",
  // Queens
  "hunters point": "long-island-city",
  "queensbridge": "long-island-city",
  "ravenswood": "astoria",
  "ditmars steinway": "astoria",
};

const BOROUGH_BY_CODE = { 1: "manhattan", 2: "bronx", 3: "brooklyn", 4: "queens", 5: "staten-island" };

/** NYC zip prefixes. StreetEasy has no zip-level data, so a zip search reports its borough. */
const _boroughForZip = (zip) => {
  const z = String(zip || "").trim().slice(0, 5);
  if (!/^\d{5}$/.test(z)) return "";
  const prefix = z.slice(0, 3);
  if (["100", "101", "102"].indexOf(prefix) >= 0) return "manhattan";
  if (prefix === "103") return "staten-island";
  if (prefix === "104") return "bronx";
  if (prefix === "112") return "brooklyn";
  if (["110", "111", "113", "114", "116"].indexOf(prefix) >= 0) return "queens";
  return "";
};

/** Building keys carry the borough code: `b-1-134-e-93rd-st-10128`. */
const _boroughForBuildingKey = (key) => {
  const match = /^b-(\d)-/.exec(String(key || ""));
  return match ? BOROUGH_BY_CODE[match[1]] || "" : "";
};

/**
 * Slugs worth trying for a set of search tags, most specific first. Everything that cannot resolve
 * to a neighborhood or borough ends at `nyc`, so a report always has data to show.
 */
export const _seCandidateSlugs = (searchTags) => {
  const tags = Array.isArray(searchTags) ? searchTags : [];
  const neighborhoods = [];
  const boroughs = [];

  tags.forEach((tag) => {
    if (!tag || !tag.type) return;
    if (tag.type === "neighborhood" && tag.data) {
      const hood = String(tag.data.hood || "").toLowerCase();
      if (SE_NEIGHBORHOOD_ALIASES[hood]) neighborhoods.push(SE_NEIGHBORHOOD_ALIASES[hood]);
      if (hood) neighborhoods.push(slugify(hood));
      if (tag.data.boro) boroughs.push(slugify(tag.data.boro));
    } else if (tag.type === "borough") {
      boroughs.push(slugify(tag.data));
    } else if (tag.type === "zip") {
      boroughs.push(_boroughForZip(tag.data));
    } else if (tag.type === "building" && tag.data) {
      boroughs.push(_boroughForBuildingKey(tag.data.key));
    } else if (tag.data && tag.data.boro) {
      boroughs.push(slugify(tag.data.boro));
    }
  });

  const all = neighborhoods.concat(boroughs).concat(["nyc"]);
  return all.filter((slug, i) => slug && all.indexOf(slug) === i);
};

/**
 * What the user actually asked for, so the page can say when the report answers at a coarser
 * geography — a Carnegie Hill search reported as Upper East Side, a zip reported as its borough.
 */
export const _seRequestedSlug = (searchTags) => {
  const tags = Array.isArray(searchTags) ? searchTags : [];
  const order = ["building", "area", "zip", "neighborhood", "borough"];
  for (let i = 0; i < order.length; i++) {
    const tag = tags.filter((t) => t && t.type === order[i])[0];
    if (!tag) continue;
    if (order[i] === "neighborhood") return slugify(tag.data && tag.data.hood);
    if (order[i] === "borough") return slugify(tag.data);
    return order[i]; // zip / building / polygon never match a StreetEasy slug
  }
  return "";
};

/** Pick the most specific area the core actually returned. */
export const _sePickArea = (docs, candidates) => {
  const bySlug = {};
  (docs || []).forEach((d) => {
    if (d && d.area_slug && !bySlug[d.area_slug]) bySlug[d.area_slug] = d;
  });
  for (let i = 0; i < candidates.length; i++) {
    if (bySlug[candidates[i]]) return bySlug[candidates[i]];
  }
  return false;
};

/** "2026-07" -> "2025-07" */
export const _sePriorYearMonth = (monthStr) => {
  const parts = String(monthStr || "").split("-");
  if (parts.length !== 2) return "";
  return `${parseInt(parts[0], 10) - 1}-${parts[1]}`;
};

/** "2026-07" -> "July 2026", for headings and copy. */
export const _seMonthLabel = (monthStr) => {
  const parts = String(monthStr || "").split("-");
  if (parts.length !== 2) return "";
  const names = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  const month = names[parseInt(parts[1], 10) - 1];
  return month ? `${month} ${parts[0]}` : parts[0];
};

/**
 * Latest month at or before the report's end date. The picker still offers ranges, but a snapshot
 * series can only answer for a month, so the range's end decides which snapshot is shown.
 */
export const _seMonthForDate = (date) => {
  const d = date instanceof Date && !isNaN(date.getTime()) ? date : new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

export const _seAreaLabel = (area) => {
  if (!area) return "New York City";
  if (area.area_type === "city") return "New York City";
  return _capitalizeText(area.area_name || area.area_slug || "");
};

/** Percent change, or null when either side is missing — never render a change against nothing. */
export const _sePctChange = (current, prior) => {
  if (!(current > 0) || !(prior > 0)) return null;
  return Math.round(((current - prior) / prior) * 1000) / 10;
};

/**
 * Intervals this data can honestly support. Weekly is absent on purpose: the source has nothing
 * below a month.
 */
export const SE_INTERVALS = ["month", "quarter", "year"];

/** The report's date control speaks in Solr gaps; map them onto the intervals above. */
export const _seIntervalForGap = (gap) => {
	if (gap === "+3MONTH") return "quarter";
	if (gap === "+1YEAR") return "year";
	return "month";
};

/** Which bucket a month belongs to: "2026-07" | "2026-Q3" | "2026". */
export const _seBucketKey = (monthStr, interval) => {
	const parts = String(monthStr || "").split("-");
	if (parts.length !== 2) return "";
	if (interval === "year") return parts[0];
	if (interval === "quarter") return `${parts[0]}-Q${Math.floor((parseInt(parts[1], 10) - 1) / 3) + 1}`;
	return monthStr;
};

export const _seBucketLabel = (bucketKey) => {
	if (/^\d{4}$/.test(bucketKey)) return bucketKey;
	// "2Q 2026", the form the market reports use, not "Q2 2026".
	if (/^\d{4}-Q\d$/.test(bucketKey)) return `${bucketKey.slice(6)}Q ${bucketKey.slice(0, 4)}`;
	return _seMonthLabel(bucketKey);
};

/** Same bucket, one year earlier. */
export const _sePriorBucketKey = (bucketKey) => {
	if (/^\d{4}$/.test(bucketKey)) return String(parseInt(bucketKey, 10) - 1);
	if (/^\d{4}-Q\d$/.test(bucketKey)) return `${parseInt(bucketKey.slice(0, 4), 10) - 1}${bucketKey.slice(4)}`;
	return _sePriorYearMonth(bucketKey);
};

const SE_AGGREGATE_FIELDS = ["median_asking_rent", "rental_inventory", "discount_share"]
	.reduce((fields, base) => fields.concat(["all"].concat(SE_BEDROOMS.map(b => b.key)).map(sfx => `${base}_${sfx}`)), []);

/**
 * Collapse a period's monthly snapshots into one figure per metric, by averaging.
 *
 * Averaging is the only defensible option here, and it changes what the numbers mean:
 *
 *   - Rents become an AVERAGE OF MONTHLY MEDIANS, not a quarterly median. A true period median
 *     needs the underlying listings, which the source does not publish. On Manhattan the two
 *     differ by under 2% (2026 Q2: $4,923 averaged vs $4,965 at quarter end), so the summary is
 *     sound — but the page has to say which it is.
 *   - Supply is a STOCK, not a flow: `rental_inventory` counts listings on the market that month,
 *     so a listing sitting for a quarter appears in all three. It is averaged, never summed.
 *   - Shares are averaged the same way.
 */
export const _seAggregate = (docs) => {
	const rows = (docs || []).filter(Boolean);
	if (!rows.length) return false;
	if (rows.length === 1) return Object.assign({}, rows[0], { _months: 1 });

	const out = Object.assign({}, rows[rows.length - 1], { _months: rows.length });
	SE_AGGREGATE_FIELDS.forEach((field) => {
		const values = rows.map(r => r[field]).filter(v => typeof v === "number" && v > 0);
		out[field] = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
	});
	return out;
};

/**
 * Group a month series into interval buckets, newest last. Each bucket carries its aggregate plus
 * the months that went into it, so a page can say "average of 3 months" where that matters.
 */
export const _seBucketSeries = (months, interval) => {
	const order = [];
	const byKey = {};
	(months || []).forEach((doc) => {
		const key = _seBucketKey(doc.month_str, interval);
		if (!key) return;
		if (!byKey[key]) { byKey[key] = []; order.push(key); }
		byKey[key].push(doc);
	});
	return order.map(key => ({
		key,
		label: _seBucketLabel(key),
		months: byKey[key],
		stats: _seAggregate(byKey[key]),
	}));
};

/** Months a complete bucket holds. */
export const _seExpectedMonths = (bucketKey) => {
	if (/^\d{4}$/.test(bucketKey)) return 12;
	if (/^\d{4}-Q\d$/.test(bucketKey)) return 3;
	return 1;
};

/**
 * The prior-year period, restricted to the SAME calendar months the current period actually has.
 *
 * Without this the newest period is unfair to itself: in July, "Q3 2026" holds one month and would
 * be set against a complete three-month Q3 2025, and "2026" (7 months, no winter) against a full
 * 2025 — seasonal rents make that a fabricated decline. Matching the months compares July to July
 * and Jan-Jul to Jan-Jul.
 */
export const _sePriorPeriodStats = (currentMonths, allMonths) => {
	const wanted = (currentMonths || []).map(d => _sePriorYearMonth(d.month_str));
	const docs = (allMonths || []).filter(d => wanted.indexOf(d.month_str) >= 0);
	return docs.length ? _seAggregate(docs) : false;
};

/** "2026 YTD" / "Q3 2026 to date" when the newest period is still filling up. */
export const _sePeriodLabel = (bucket) => {
	if (!bucket) return "";
	const complete = bucket.months.length >= _seExpectedMonths(bucket.key);
	if (complete) return bucket.label;
	if (/^\d{4}$/.test(bucket.key)) return `${bucket.key} YTD`;
	return `${bucket.label} to date`;
};

/**
 * Which period a report shows for its interval.
 *
 *   "current"  — the newest bucket, part-finished (QTD, YTD). Compared against the same calendar
 *                months a year earlier, so a 1-month QTD meets a 1-month span, not a full quarter.
 *   "complete" — the newest bucket that ran its full length (2Q 2026, 2025). Compared against the
 *                equivalent complete period a year earlier.
 *
 * Both are wanted: QTD answers "where are we now", the completed quarter answers "what did the
 * last full quarter do" without a partial period muddying it.
 */
export const SE_PERIOD_MODES = ["current", "complete"];

export const _seSelectBucket = (series, mode) => {
	const buckets = series || [];
	if (!buckets.length) return false;
	if (mode !== "complete") return buckets[buckets.length - 1];
	const complete = buckets.filter(b => b.months.length >= _seExpectedMonths(b.key));
	return complete.length ? complete[complete.length - 1] : buckets[buckets.length - 1];
};

/** Labels for the period buttons, derived from today so they name a real period. */
export const _seCompletePeriodLabel = (interval, date) => {
	const d = date instanceof Date && !isNaN(date.getTime()) ? date : new Date();
	const year = d.getUTCFullYear();
	if (interval === "year") return String(year - 1);
	const quarter = Math.floor(d.getUTCMonth() / 3) + 1;
	return quarter === 1 ? `4Q ${year - 1}` : `${quarter - 1}Q ${year}`;
};
